import NextLink from 'next/link';

import { useSectionBrower } from 'components/DealerPage/components/SectionBrowser/SectionBrowser.hook';

import { Label } from 'components/Toolkit/Inputs/Label';

import { formatNumber } from 'helpers/formatting';

import type {
  BackButtonProps,
  SectionBrowserProps,
  SectionProps,
} from 'components/DealerPage/components/SectionBrowser/SectionBrowser.typed';

import * as Styled from 'components/DealerPage/components/SectionBrowser/SectionBrowser.styled';

function Section({ link, section, selected, onClick }: SectionProps) {
  return (
    <NextLink href={link} passHref shallow legacyBehavior>
      <Styled.Item
        href={link}
        selected={selected}
        onClick={() => onClick && onClick(section.name)}
      >
        <span>{section.displayName}</span>
        {typeof section.searchMatches !== 'undefined' && (
          <Styled.Count>{formatNumber(section.searchMatches)}</Styled.Count>
        )}
      </Styled.Item>
    </NextLink>
  );
}

function BackButton({ link, section, onClick }: BackButtonProps) {
  return (
    <NextLink href={link} passHref shallow legacyBehavior>
      <Styled.Back href={link} onClick={onClick}>
        <Styled.BackArrow width={12} height={12} />
        <span>{section.displayName}</span>
      </Styled.Back>
    </NextLink>
  );
}

function SectionBrowser({
  sections,
  currentSection,
  parentSection,
  onSelectSection,
  onSelectBack,
  collapsible = true,
  baseUrl,
}: SectionBrowserProps) {
  const {
    collapsed,
    setCollapsed,
    handleSelectBack,
    shouldCollapse,
    handleSelectSection,
    getLink,
    showSubsections,
    filteredSubsections,
  } = useSectionBrower({
    sections,
    parentSection,
    onSelectSection,
    onSelectBack,
    collapsible,
    baseUrl,
  });

  return (
    <>
      <Label>Section</Label>
      <div>
        {parentSection && (
          <div>
            <BackButton
              link={getLink(parentSection.name)}
              section={parentSection}
              onClick={handleSelectBack}
            />
            <Styled.SubsectionsHeading>Subsections</Styled.SubsectionsHeading>
          </div>
        )}
        <Styled.Collapsible collapsed={shouldCollapse ? collapsed : false}>
          <ul>
            <li>
              <Section
                link={getLink(currentSection.name)}
                selected={true}
                section={currentSection}
              />
            </li>
            {showSubsections &&
              filteredSubsections.map((item, index) => (
                <li key={index}>
                  <Section
                    link={getLink(item.name)}
                    selected={false}
                    section={item}
                    onClick={() => handleSelectSection(item.name)}
                  />
                </li>
              ))}
          </ul>
          {shouldCollapse && (
            <Styled.CollapseButton
              collapsed={collapsed}
              onClick={() => setCollapsed(!collapsed)}
            >
              <span>
                {collapsed
                  ? `View all ${filteredSubsections.length} sections`
                  : 'View less sections'}
              </span>
              <Styled.CollapseButtonIcon width={16} height={16} />
            </Styled.CollapseButton>
          )}
        </Styled.Collapsible>
      </div>
    </>
  );
}

export { SectionBrowser };
